import { useCallback, useEffect, useState } from 'react'
import { collectNpc, getCharacterRelationApi, likeNpc } from '@/lib/api/home'
import { useUserStore } from '@/store/userStore'
import type { CharacterChatViewData } from '@/types/characterChat'

function toCount(value: number | string | null | undefined): number {
  const count = Number(value)
  return Number.isFinite(count) ? Math.max(0, count) : 0
}

const useCharacterRelation = ({ character }: { character: CharacterChatViewData }) => {
  const [liked, setLiked] = useState(false)
  const [collected, setCollected] = useState(false)
  const [likeCount, setLikeCount] = useState(() =>
    toCount(character.stats?.likes ?? character.likeCount),
  )
  const [favoriteCount, setFavoriteCount] = useState(() => toCount(character.stats?.favorites))
  const [isLikePending, setIsLikePending] = useState(false)
  const [isCollectPending, setIsCollectPending] = useState(false)
  const { isLogin } = useUserStore()

  // 聊天页会先用入口预置数据（无 stats）挂载，详情接口异步返回后再同步计数；
  // useState 初始化只执行一次，这里必须跟随 props 变化补齐。
  const statsLikes = character.stats?.likes
  const statsFavorites = character.stats?.favorites

  useEffect(() => {
    if (statsLikes !== undefined) setLikeCount(toCount(statsLikes))
  }, [statsLikes])

  useEffect(() => {
    if (statsFavorites !== undefined) setFavoriteCount(toCount(statsFavorites))
  }, [statsFavorites])

  useEffect(() => {
    async function initCharacterRelation() {
      try {
        const response = await getCharacterRelationApi({ id: character.id })
        if (response.code !== 200) {
          console.error('Failed to load character relation:', response.msg)
          return
        }

        setLiked(response.data.liked)
        setCollected(response.data.collected)
      } catch (error) {
        console.error('Failed to load character relation:', error)
      }
    }
    if (isLogin) {
      void initCharacterRelation()
    }
  }, [character.id, isLogin])

  const handleLikeClick = useCallback(async () => {
    if (isLikePending) return

    setIsLikePending(true)
    try {
      const response = await likeNpc(character.id)
      if (response.code !== 200) {
        console.error('Failed to update character like:', response.msg)
        return
      }

      const nextLiked = response.data
      if (nextLiked !== liked) {
        setLikeCount((count) => Math.max(0, count + (nextLiked ? 1 : -1)))
      }
      setLiked(nextLiked)
    } catch (error) {
      console.error('Failed to update character like:', error)
    } finally {
      setIsLikePending(false)
    }
  }, [character.id, isLikePending, liked])

  const handleCollectClick = useCallback(async () => {
    if (isCollectPending) return

    setIsCollectPending(true)
    try {
      const response = await collectNpc(character.id)
      if (response.code !== 200) {
        console.error('Failed to update character favorite:', response.msg)
        return
      }

      setFavoriteCount(toCount(response.data))
      setCollected((current) => !current)
    } catch (error) {
      console.error('Failed to update character favorite:', error)
    } finally {
      setIsCollectPending(false)
    }
  }, [character.id, isCollectPending])

  return {
    liked,
    collected,
    likeCount,
    favoriteCount,
    isLikePending,
    isCollectPending,
    handleLikeClick,
    handleCollectClick,
  }
}

export default useCharacterRelation
